import React from 'react';
import { Collapse, Nav } from 'react-bootstrap';
import { Code } from 'react-content-loader';
import camelCaseRecursive from 'camelcase-keys-recursive';
import Icon from '../Icons';
import getFolderAbility from './ability';
import findNodeRecursive from '../../screens/Shared/DirectoryNode/helpers';
import { useDirectoryNodes } from './DirectoryNodesContext';

const FolderItem = ({
  folder,
  parentId,
  handleButtonClick,
  ability,
  canMoveFile,
  directoryNodes,
  treeLevel = 0,
  handleFirstNodeRequest,
  handleOpenCenterModal,
  handleLoadMoreDocuments,
  maxDepth,
  setParentId
}) => {
  const permissions = getFolderAbility(camelCaseRecursive(folder), ability);

  const { closeParentAndChildren, openFolderAndCloseSiblings, setSelectedFolder } = useDirectoryNodes();

  const toggleExpand = async () => {
    if (!folder.isLoaded) {
      setParentId(parentId || folder.id);
      handleFirstNodeRequest(folder.id, true);
      return;
    }

    if (folder.isLoaded && !folder.isOpen) {
      openFolderAndCloseSiblings(folder.id);
      setParentId(parentId || folder.id);
      setSelectedFolder(folder.id);
      return;
    }

    if (folder.isOpen) {
      closeParentAndChildren(folder.id);

      const parent = findNodeRecursive(directoryNodes, folder.dependFrom?.id);
      if (parent) {
        setSelectedFolder(parent.id);
        setParentId(folder.dependFrom?.id);
      } else {
        setSelectedFolder(null);
        setParentId(null);
      }
    }
  };

  return (
    <div className="">
      <Nav.Item className="folder-box nav-item__folder">
        <Nav.Link onClick={toggleExpand} className={`${folder.isOpen ? 'active' : ''}`}>
          <div className="d-flex align-items-center">
            <Icon
              icon={folder.isOpen ? 'chevron-down' : 'chevron-forward'}
              className="mr-2"
              style={{ cursor: 'pointer' }}
            />
            <Icon icon={folder.isOpen ? 'folder-open' : 'folder'} className="mr-2" />
            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{folder.title}</span>
          </div>
        </Nav.Link>
      </Nav.Item>
      {folder.isLoading ? (
        <div id="box" className="code-loader" style={{ marginLeft: `${treeLevel * 20}px` }}>
          <Code />
        </div>
      ) : (
        <Collapse in={folder.isOpen}>
          <div>
            <IterateArray
              isParent={false}
              childrenArray={folder.children || []}
              documentsArray={folder.documents || []}
              handleOpenCenterModal={handleOpenCenterModal}
              parentName={folder.name}
              parentId={folder.id}
              permitedActions={permissions}
              permitedCreations={permissions}
              handleButtonClick={handleButtonClick}
              maxDepth={maxDepth - 1}
              directoryNodes={directoryNodes}
              canMoveFile={canMoveFile}
              ability={ability}
              treeLevel={treeLevel + 1}
              handleFirstNodeRequest={handleFirstNodeRequest}
              handleLoadMoreDocuments={handleLoadMoreDocuments}
              nextPage={folder.nextPage}
              setSelectedFolder={setSelectedFolder}
              setParentId={setParentId}
            />
          </div>
        </Collapse>
      )}
    </div>
  );
};

const IterateArray = ({
  canMoveFile,
  childrenArray = [],
  directoryNodes,
  handleButtonClick,
  handleOpenCenterModal,
  maxDepth,
  parentId,
  permitedActions,
  permitedCreations,
  root,
  ability,
  handleFirstNodeRequest,
  handleLoadMoreDocuments,
  treeLevel = 0,
  setParentId
}) => {
  const folderContent = () => {
    return (
      <>
        <Nav variant="pills" className="flex-column dropdown-responsive">
          {childrenArray?.map((folder, index) => (
            <FolderItem
              key={`folder-${folder.id}-${index.toString()}`}
              setParentId={setParentId}
              folder={folder}
              parentId={parentId}
              handleButtonClick={handleButtonClick}
              ability={ability}
              canMoveFile={canMoveFile}
              directoryNodes={directoryNodes}
              treeLevel={treeLevel}
              handleFirstNodeRequest={handleFirstNodeRequest}
              handleOpenCenterModal={handleOpenCenterModal}
              handleLoadMoreDocuments={handleLoadMoreDocuments}
              maxDepth={maxDepth}
              permitedActions={permitedActions}
              permitedCreations={permitedCreations}
              hasPermissionsToAddFiles={permitedCreations.find(e => e === 'node' || e === 'file')}
            />
          ))}
        </Nav>
      </>
    );
  };

  return (
    <div className="grid-content mt-1 mb-0">
      <div className={`docs-box mb-0 ${root ? 'docs-box ' : 'pt-0'} tree-level level-${treeLevel}`}>
        {folderContent()}
      </div>
    </div>
  );
};

export { FolderItem };
export default IterateArray;
